import { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TabsContent, Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Filter, X, Trash2, History, Calendar, Camera, Search, Eye } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { REVISION_TYPES } from "@/constants/revisionTypes";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { normalizeSearchText } from '@/utils/stringUtils';

export default function RevisionsTab({
  revisions,
  loadingRevisions,
  setRevisionDialogOpen,
  setSelectedRevision,
  onDeleteRevision = null,
  onDeleteCompletedRevisionHistory = null,
  userRole = null,
  parks = [],
  personnel = [],
  personnelId = null,
  regions = []
}) {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  // Filtreleme state'leri
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all");
  
  // Kategori seçenekleri: ortak 16 tür + verideki olası legacy türler
  const categories = useMemo(() => {
    const dataCats = revisions.map(r => r.category).filter(Boolean);
    const cats = [...new Set([...REVISION_TYPES, ...dataCats])];
    return cats.sort((a, b) => a.localeCompare(b, 'tr'));
  }, [revisions]);
  
  const statuses = ["Onay Bekliyor", "Onaylandı", "Reddedildi", "Ertelendi", "Tamamlandı"];
  const urgencies = ["ÇOK ACİL", "ACİL", "ORTA", "DÜŞÜK"];
  
  // Filtrelenmiş revizeler (görünürlük backend tarafında park/bölge bazında
  // uygulanır; burada yalnızca kategori/durum/aciliyet filtreleri kalır)
  const filteredRevisions = useMemo(() => {
    return revisions.filter((r) => {
      if (filterCategory !== "all" && r.category !== filterCategory) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterUrgency !== "all" && r.urgency !== filterUrgency) return false;
      return true;
    });
  }, [revisions, filterCategory, filterStatus, filterUrgency]);

  const hasFilters = filterCategory !== "all" || filterStatus !== "all" || filterUrgency !== "all";
  
  const clearFilters = () => {
    setFilterCategory("all");
    setFilterStatus("all");
    setFilterUrgency("all");
  };

  const normalizeRole = (role) => {
    const raw = String(role || "").trim().toLowerCase();
    const roleMap = {
      "müdür": "mudur",
      "mudur": "mudur",
      "müdür yardımcısı": "mudur_yardimcisi",
      "mudur yardimcisi": "mudur_yardimcisi",
      "mudur_yardimcisi": "mudur_yardimcisi",
      "bölge mühendisi": "bolge_muhendisi",
      "bolge muhendisi": "bolge_muhendisi",
      "bolge_muhendisi": "bolge_muhendisi",
      "formen": "formen",
      "park görevlisi": "park_gorevlisi",
      "park gorevlisi": "park_gorevlisi",
      "park_gorevlisi": "park_gorevlisi",
      "çim ekibi": "cim_ekibi",
      "çim_ekibi": "cim_ekibi",
      "cim ekibi": "cim_ekibi",
      "cim_ekibi": "cim_ekibi",
    };
    return roleMap[raw] || raw;
  };

  const roleDisplay = {
    formen: "FORMEN",
    bolge_muhendisi: "BM",
    mudur_yardimcisi: "MY",
    mudur: "MÜDÜR",
  };

  const personnelById = useMemo(() => {
    const m = new Map();
    (personnel || []).forEach((p) => {
      if (p?.id) m.set(String(p.id), p);
    });
    return m;
  }, [personnel]);

  const normalizePersonName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .replaceAll("ı", "i")
      .replaceAll("İ", "i")
      .replaceAll("ğ", "g")
      .replaceAll("ü", "u")
      .replaceAll("ş", "s")
      .replaceAll("ö", "o")
      .replaceAll("ç", "c");

  const personnelByName = useMemo(() => {
    const m = new Map();
    (personnel || []).forEach((p) => {
      const key = normalizePersonName(p?.name);
      if (key && !m.has(key)) m.set(key, p);
    });
    return m;
  }, [personnel]);

  const parkById = useMemo(() => {
    const m = new Map();
    (parks || []).forEach((park) => {
      if (park?.id) m.set(String(park.id), park);
    });
    return m;
  }, [parks]);

  const resolveCurrentLevel = (revision) => {
    const directLevel = normalizeRole(revision?.current_level || revision?.current_approval_level);
    if (["formen", "bolge_muhendisi", "mudur_yardimcisi", "mudur"].includes(directLevel)) {
      return directLevel;
    }

    // Fallback: current_level yoksa/bozuksa son entry seviyesinden bir sonraki role türet.
    const entries = Array.isArray(revision?.entries) ? revision.entries : [];
    const lastLevel = normalizeRole(entries?.[entries.length - 1]?.level);
    const nextLevelByLastEntry = {
      park_gorevlisi: "formen",
      cim_ekibi: "formen",
      formen: "bolge_muhendisi",
      bolge_muhendisi: "mudur_yardimcisi",
      mudur_yardimcisi: "mudur",
      mudur: "mudur",
    };
    return nextLevelByLastEntry[lastLevel] || null;
  };

  const currentReviewerInfo = (revision) => {
    const finalStatuses = ["Onaylandı", "Reddedildi", "Tamamlandı", "Ertelendi"];
    if (finalStatuses.includes(revision?.status)) return null;

    const level = resolveCurrentLevel(revision);
    if (!level) return null;

    const revisionEntries = Array.isArray(revision?.entries) ? revision.entries : [];
    const firstEntry = revisionEntries[0] || null;
    const lastEntry = revisionEntries[revisionEntries.length - 1] || null;
    const park = parkById.get(String(revision?.park_id || ""));
    const assignedIds = Array.isArray(park?.assigned_personnel_ids) ? park.assigned_personnel_ids : [];
    const firstAssigned = assignedIds.length ? personnelById.get(String(assignedIds[0])) : null;

    const byIdOrName = (idValue, nameValue) =>
      (idValue ? personnelById.get(String(idValue)) : null) ||
      (nameValue ? personnelByName.get(normalizePersonName(nameValue)) : null) ||
      null;

    const creator =
      byIdOrName(revision?.created_by_id, revision?.created_by_name) ||
      byIdOrName(firstEntry?.person_id, firstEntry?.person_name) ||
      firstAssigned;

    const findByRole = (targetRole) =>
      (personnel || []).find((p) => normalizeRole(p?.role) === targetRole) || null;

    let reviewerId = null;

    if (level === "formen") {
      reviewerId =
        revision?.current_reviewer_id ||
        creator?.formen_id ||
        null;
      if (!reviewerId) {
        const source = byIdOrName(lastEntry?.person_id, lastEntry?.person_name);
        reviewerId = source?.formen_id || null;
      }
      if (!reviewerId) reviewerId = firstAssigned?.formen_id || null;
    } else if (level === "bolge_muhendisi") {
      reviewerId =
        revision?.current_reviewer_id ||
        creator?.bolge_muhendisi_id ||
        null;
      if (!reviewerId && creator?.formen_id) {
        const formen = personnelById.get(String(creator.formen_id));
        reviewerId = formen?.bolge_muhendisi_id || null;
      }
      if (!reviewerId) {
        const formenEntry = [...revisionEntries]
          .reverse()
          .find((e) => normalizeRole(e?.level) === "formen");
        const formen = byIdOrName(formenEntry?.person_id, formenEntry?.person_name);
        reviewerId = formen?.bolge_muhendisi_id || null;
      }
      if (!reviewerId) reviewerId = firstAssigned?.bolge_muhendisi_id || null;
    } else if (level === "mudur_yardimcisi") {
      reviewerId =
        revision?.current_reviewer_id ||
        creator?.mudur_yardimcisi_id ||
        null;
      if (!reviewerId && creator?.bolge_muhendisi_id) {
        const bm = personnelById.get(String(creator.bolge_muhendisi_id));
        reviewerId = bm?.mudur_yardimcisi_id || null;
      }
      if (!reviewerId) {
        const bmEntry = [...revisionEntries]
          .reverse()
          .find((e) => normalizeRole(e?.level) === "bolge_muhendisi");
        const bm = byIdOrName(bmEntry?.person_id, bmEntry?.person_name);
        reviewerId = bm?.mudur_yardimcisi_id || null;
      }
      if (!reviewerId) reviewerId = firstAssigned?.mudur_yardimcisi_id || null;
    } else if (level === "mudur") {
      const mudur = findByRole("mudur");
      reviewerId = mudur?.id || null;
    }

    const reviewer = reviewerId ? personnelById.get(String(reviewerId)) : null;
    const reviewerName = reviewer?.name || revision?.current_reviewer_name || null;
    return {
      levelLabel: roleDisplay[level] || String(level || "").toUpperCase(),
      name: reviewerName,
    };
  };

  const displayRevisionStatus = (revision) => {
    const rawStatus = String(revision?.status || "");
    const finalStatuses = ["Onaylandı", "Reddedildi", "Tamamlandı", "Ertelendi"];
    if (finalStatuses.includes(rawStatus)) return rawStatus;

    const level = resolveCurrentLevel(revision);
    if (level === "formen") return "İncelemede";
    if (level === "bolge_muhendisi") return "Değerlendirmede";
    if (level === "mudur_yardimcisi") return "Değerlendirmede";
    if (level === "mudur") return "Onay Bekliyor";

    return rawStatus || "İncelemede";
  };

  // Yetki kontrolüne göre parkları filtrele
  const filteredParks = useMemo(() => {
    if (!parks || parks.length === 0) return [];
    
    if (isAdmin || isMudur) {
      return parks; // Müdür tüm parkları görür
    }
    
    if (isMudurYardimcisi) {
      // Müdür Yardımcısı - kendine bağlı parkları görür
      const currentPersonnel = personnel.find(p => p.id === personnelId);
      if (!currentPersonnel) return [];
      
      // Kendine bağlı bölgeler (assigned_mudur_yardimcisi_id veya mudur_yardimcisi_id)
      const myRegionIds = regions
        .filter(r => (r.assigned_mudur_yardimcisi_id === personnelId) || (r.mudur_yardimcisi_id === personnelId))
        .map(r => r.id);
      
      // Kendine bağlı formenler
      const myFormenIds = personnel
        .filter(p => p.mudur_yardimcisi_id === personnelId && p.role === 'formen')
        .map(p => p.id);
      
      // Kendine bağlı bölge mühendisleri
      const myBMIds = personnel
        .filter(p => p.mudur_yardimcisi_id === personnelId && p.role === 'bolge_muhendisi')
        .map(p => p.id);
      
      // Kendine bağlı park görevlileri
      const myParkGorevlisiIds = personnel
        .filter(p => p.mudur_yardimcisi_id === personnelId && p.role === 'park_gorevlisi')
        .map(p => p.id);
      
      return parks.filter(park => {
        // Bölge kontrolü
        if (myRegionIds.length > 0 && myRegionIds.includes(park.region_id)) return true;
        
        // Direkt atama
        if (park.assigned_mudur_yardimcisi_id === personnelId) return true;
        
        // BM kontrolü
        if (park.assigned_bolge_muhendisi_id && myBMIds.includes(park.assigned_bolge_muhendisi_id)) return true;
        
        // Formen kontrolü - park.assigned_personnel_ids içinde formen varsa
        if (park.assigned_personnel_ids && park.assigned_personnel_ids.some(id => myFormenIds.includes(id))) return true;
        
        // Park görevlisi kontrolü - park.assigned_personnel_ids içinde park görevlisi varsa
        if (park.assigned_personnel_ids && park.assigned_personnel_ids.some(id => myParkGorevlisiIds.includes(id))) return true;
        
        // Formen'in altındaki personelin parkları
        const myFormenSubordinates = personnel.filter(p => 
          myFormenIds.includes(p.formen_id)
        );
        const myFormenSubordinateIds = myFormenSubordinates.map(s => s.id);
        if (park.assigned_personnel_ids && park.assigned_personnel_ids.some(id => myFormenSubordinateIds.includes(id))) return true;
        
        return false;
      });
    }
    
    if (isBolgeMuhendisi) {
      // Bölge Mühendisi - kendi bölgeleri + altındaki formenlerin ve
      // park görevlilerinin parkları
      const assignedRegionIds = regions
        .filter(r => r.assigned_bolge_muhendisi_id === personnelId)
        .map(r => r.id);

      // Altındaki formenler (doğrudan bağlı) ve onların altındaki personel
      const myFormenIds = personnel
        .filter(p => p.bolge_muhendisi_id === personnelId && p.role === 'formen')
        .map(p => p.id);
      const formenSubordinateIds = personnel
        .filter(p => myFormenIds.includes(p.formen_id))
        .map(p => p.id);
      // Doğrudan BM'ye bağlı astlar (park görevlileri vb.)
      const directSubordinateIds = personnel
        .filter(p => p.bolge_muhendisi_id === personnelId)
        .map(p => p.id);
      const mySubordinateIds = new Set([
        ...myFormenIds,
        ...formenSubordinateIds,
        ...directSubordinateIds,
      ]);

      return parks.filter(park =>
        assignedRegionIds.includes(park.region_id) ||
        park.assigned_bolge_muhendisi_id === personnelId ||
        (park.assigned_personnel_ids || []).some(id => mySubordinateIds.has(id))
      );
    }
    
    if (isFormen) {
      // Formen - kendine bağlı parkları görür
      const currentPersonnel = personnel.find(p => p.id === personnelId);
      if (!currentPersonnel) return [];
      
      return parks.filter(park => {
        // Direkt atama
        if (park.assigned_personnel_ids && park.assigned_personnel_ids.includes(personnelId)) return true;
        
        // Altındaki personelin parkları
        const subordinates = personnel.filter(p => p.formen_id === personnelId);
        const subordinateIds = subordinates.map(s => s.id);
        if (park.assigned_personnel_ids && park.assigned_personnel_ids.some(id => subordinateIds.includes(id))) return true;
        
        return false;
      });
    }
    
    if (isParkGorevlisi) {
      // Park Görevlisi - sadece kendine atanmış parkları görür
      return parks.filter(park => 
        (park.assigned_personnel_ids || []).includes(personnelId)
      );
    }
    
    return [];
  }, [parks, userRole, personnelId, personnel, regions]);

  // Revize geçmişi filtreleme state'leri
  const [historyFilterPark, setHistoryFilterPark] = useState("all");
  const [historyFilterCategory, setHistoryFilterCategory] = useState("all");
  const [historyFilterDateFrom, setHistoryFilterDateFrom] = useState("");
  const [historyFilterDateTo, setHistoryFilterDateTo] = useState("");
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [selectedRevisionDetail, setSelectedRevisionDetail] = useState(null);
  const [editingRevisionHistory, setEditingRevisionHistory] = useState(null);
  const [editingRevisionHistoryPhotos, setEditingRevisionHistoryPhotos] = useState({ before: [], after: [] });
  const [uploadingHistoryEditPhotos, setUploadingHistoryEditPhotos] = useState(false);
  const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0, title: "Revize Fotoğrafı" });
  const [historyDeleteDialog, setHistoryDeleteDialog] = useState({
    open: false,
    step: 1,
    parkId: null,
    revisionId: null,
    parkName: "",
    category: "",
  });
  
  // Yetki kontrolü: Admin + müdür + müdür yardımcısı düzenleyebilir
  const canEditRevisionHistory = isAdmin || isMudur || isMudurYardimcisi;
  // Yeni revize oluşturma — revizyon.olustur yetkisine bağlı (menü revizyon.gor
  // ile de açılabildiği için "Yeni Revize" sekmesi ayrıca bu yetkiyle gizlenmeli).
  const canCreateRevision = has('revizyon.olustur');

  // Fotoğraf URL helper: relative path'i backend adresiyle tamamla.
  const backendBaseUrl = useMemo(() => {
    const raw = String(process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000").trim();
    return raw.endsWith("/") ? raw.slice(0, -1) : raw;
  }, []);

  const resolvePhotoUrl = useCallback((photo) => {
    const rawPath = typeof photo === "string" ? photo : (photo?.url || photo?.path || "");
    if (!rawPath) return "";
    if (/^https?:\/\//i.test(rawPath)) return rawPath;
    const normalizedPath = String(rawPath).startsWith("/") ? String(rawPath) : `/${String(rawPath)}`;
    return `${backendBaseUrl}${normalizedPath}`;
  }, [backendBaseUrl]);

  const openPhotoViewer = useCallback((photos, index = 0, title = "Revize Fotoğrafı") => {
    if (!Array.isArray(photos) || photos.length === 0) return;
    setPhotoViewer({ open: true, photos, index, title });
  }, []);

  const openHistoryDeleteDialog = useCallback((rev) => {
    setHistoryDeleteDialog({
      open: true,
      step: 1,
      parkId: rev?.park_id || null,
      revisionId: rev?.revision_id || null,
      parkName: rev?.park_name || "",
      category: rev?.category || "",
    });
  }, []);

  const closeHistoryDeleteDialog = useCallback(() => {
    setHistoryDeleteDialog({
      open: false,
      step: 1,
      parkId: null,
      revisionId: null,
      parkName: "",
      category: "",
    });
  }, []);

  const handleConfirmHistoryDelete = useCallback(async () => {
    if (!onDeleteCompletedRevisionHistory || !historyDeleteDialog.parkId || !historyDeleteDialog.revisionId) return;
    await onDeleteCompletedRevisionHistory(
      historyDeleteDialog.parkId,
      historyDeleteDialog.revisionId,
      { skipBrowserConfirm: true }
    );
    closeHistoryDeleteDialog();
  }, [onDeleteCompletedRevisionHistory, historyDeleteDialog, closeHistoryDeleteDialog]);
  
  // Aktif filtreler (Ara butonuna basıldığında güncellenir)
  const [activeHistoryFilterPark, setActiveHistoryFilterPark] = useState("all");
  const [activeHistoryFilterCategory, setActiveHistoryFilterCategory] = useState("all");
  const [activeHistoryFilterDateFrom, setActiveHistoryFilterDateFrom] = useState("");
  const [activeHistoryFilterDateTo, setActiveHistoryFilterDateTo] = useState("");
  const [activeHistorySearchTerm, setActiveHistorySearchTerm] = useState("");

  // Tüm revize geçmişini topla
  const allRevisionHistory = useMemo(() => {
    const history = [];
    
    filteredParks.forEach(park => {
      if (park.completed_revisions_history && Array.isArray(park.completed_revisions_history)) {
        park.completed_revisions_history.forEach(rev => {
          history.push({
            ...rev,
            park_id: park.id,
            park_name: park.name,
            park_neighborhood: park.neighborhood
          });
        });
      }
    });
    
    // Tarihe göre sırala (en yeni önce) - completed_at backend'den gelen tamamlanma tarihi
    return history.sort((a, b) => {
      const dateA = new Date(a.revision_date || a.created_at || a.completed_at || 0);
      const dateB = new Date(b.revision_date || b.created_at || b.completed_at || 0);
      return dateB - dateA;
    });
  }, [filteredParks]);

  // Filtrelenmiş revize geçmişi (aktif filtreleri kullanır)
  const filteredRevisionHistory = useMemo(() => {
    return allRevisionHistory.filter(rev => {
      // Arama filtresi
      if (activeHistorySearchTerm.trim()) {
        const searchLower = normalizeSearchText(activeHistorySearchTerm);
        const matchesPark = normalizeSearchText(rev.park_name).includes(searchLower);
        const matchesCategory = normalizeSearchText(rev.category).includes(searchLower);
        const matchesDescription = normalizeSearchText(rev.description).includes(searchLower);
        const matchesNeighborhood = normalizeSearchText(rev.park_neighborhood).includes(searchLower);
        
        if (!matchesPark && !matchesCategory && !matchesDescription && !matchesNeighborhood) {
          return false;
        }
      }
      
      // Park filtresi
      if (activeHistoryFilterPark !== "all" && rev.park_id !== activeHistoryFilterPark) return false;
      
      // Kategori filtresi
      if (activeHistoryFilterCategory !== "all" && rev.category !== activeHistoryFilterCategory) return false;
      
      // Tarih filtresi (completed_at = tamamlanma tarihi)
      const revDate = new Date(rev.revision_date || rev.created_at || rev.completed_at || 0);
      if (activeHistoryFilterDateFrom) {
        const fromDate = new Date(activeHistoryFilterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (revDate < fromDate) return false;
      }
      if (activeHistoryFilterDateTo) {
        const toDate = new Date(activeHistoryFilterDateTo);
        toDate.setHours(23, 59, 59, 999);
        if (revDate > toDate) return false;
      }
      
      return true;
    });
  }, [allRevisionHistory, activeHistoryFilterPark, activeHistoryFilterCategory, activeHistoryFilterDateFrom, activeHistoryFilterDateTo, activeHistorySearchTerm]);
  
  // Ara butonuna tıklandığında aktif filtreleri güncelle
  const handleSearchHistory = () => {
    setActiveHistoryFilterPark(historyFilterPark);
    setActiveHistoryFilterCategory(historyFilterCategory);
    setActiveHistoryFilterDateFrom(historyFilterDateFrom);
    setActiveHistoryFilterDateTo(historyFilterDateTo);
    setActiveHistorySearchTerm(historySearchTerm);
  };

  // Benzersiz parklar ve kategoriler
  const uniqueParks = useMemo(() => {
    const parkMap = new Map();
    filteredParks.forEach(park => {
      parkMap.set(park.id, { id: park.id, name: park.name, neighborhood: park.neighborhood });
    });
    return Array.from(parkMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [filteredParks]);

  const uniqueCategories = useMemo(() => {
    const dataCats = allRevisionHistory.map(r => r.category).filter(Boolean);
    const cats = [...new Set([...REVISION_TYPES, ...dataCats])];
    return cats.sort((a, b) => a.localeCompare(b, 'tr'));
  }, [allRevisionHistory]);

  const hasHistoryFilters = activeHistoryFilterPark !== "all" || activeHistoryFilterCategory !== "all" || activeHistoryFilterDateFrom || activeHistoryFilterDateTo || activeHistorySearchTerm.trim();

  const clearHistoryFilters = () => {
    setHistoryFilterPark("all");
    setHistoryFilterCategory("all");
    setHistoryFilterDateFrom("");
    setHistoryFilterDateTo("");
    setHistorySearchTerm("");
    setActiveHistoryFilterPark("all");
    setActiveHistoryFilterCategory("all");
    setActiveHistoryFilterDateFrom("");
    setActiveHistoryFilterDateTo("");
    setActiveHistorySearchTerm("");
  };

  // Tarih formatı
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                     'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Revize geçmişi düzenleme için fotoğraf yükleme
  const handleUploadHistoryEditPhoto = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const currentPhotos = editingRevisionHistoryPhotos[type] || [];
    if (files.length + currentPhotos.length > 10) {
      toast.error("Maksimum 10 fotoğraf yükleyebilirsiniz");
      return;
    }
    
    setUploadingHistoryEditPhotos(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/api/upload-revision-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return response.data.url;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      setEditingRevisionHistoryPhotos(prev => ({
        ...prev,
        [type]: [...(prev[type] || []), ...uploadedUrls]
      }));
      
      toast.success(`${uploadedUrls.length} fotoğraf yüklendi`);
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error("Fotoğraf yüklenirken hata oluştu");
    } finally {
      setUploadingHistoryEditPhotos(false);
    }
  };

  // Revize geçmişi düzenleme için fotoğraf silme
  const handleRemoveHistoryEditPhoto = (type, index) => {
    setEditingRevisionHistoryPhotos(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  // Revize geçmişi kaydetme
  const handleSaveHistoryEditedRevision = async () => {
    if (!editingRevisionHistory || !editingRevisionHistory.park_id) return;
    
    if (!editingRevisionHistory.date || !editingRevisionHistory.category) {
      toast.error("Lütfen tarih ve kategori girin");
      return;
    }
    
    setUploadingHistoryEditPhotos(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const parkId = editingRevisionHistory.park_id;
      const revisionId = editingRevisionHistory.revision_id;
      
      // Park'ın mevcut revize geçmişini al
      const currentPark = parks.find(p => p.id === parkId);
      const currentHistory = currentPark?.completed_revisions_history || [];
      
      // İlgili revize kaydını bul ve güncelle
      const updatedHistory = currentHistory.map(rev => {
        if (rev.revision_id === revisionId) {
          // Düzenleme geçmişi oluştur
          const editHistory = rev.edit_history || [];
          const newEditEntry = {
            edited_at: new Date().toISOString(),
            edited_by: userRole,
            changes: {
              date: rev.revision_date !== editingRevisionHistory.date ? { from: rev.revision_date, to: editingRevisionHistory.date } : null,
              category: rev.category !== editingRevisionHistory.category ? { from: rev.category, to: editingRevisionHistory.category } : null,
              description: rev.description !== editingRevisionHistory.description ? { from: rev.description, to: editingRevisionHistory.description } : null,
              photos_before_count: (rev.photos?.before || []).length !== editingRevisionHistoryPhotos.before.length ? { from: (rev.photos?.before || []).length, to: editingRevisionHistoryPhotos.before.length } : null,
              photos_after_count: (rev.photos?.after || []).length !== editingRevisionHistoryPhotos.after.length ? { from: (rev.photos?.after || []).length, to: editingRevisionHistoryPhotos.after.length } : null
            }
          };
          
          return {
            ...rev,
            revision_date: editingRevisionHistory.date,
            category: editingRevisionHistory.category,
            description: editingRevisionHistory.description,
            photos: {
              before: editingRevisionHistoryPhotos.before,
              after: editingRevisionHistoryPhotos.after
            },
            edit_history: [...editHistory, newEditEntry],
            last_edited_at: new Date().toISOString(),
            last_edited_by: userRole
          };
        }
        return rev;
      });
      
      // Backend'e gönder (personnel_id middleware'de gölge admin kontrolü için)
      const q = personnelId ? `?personnel_id=${encodeURIComponent(personnelId)}` : "";
      const response = await axios.put(`${API}/api/parks/${parkId}${q}`, {
        completed_revisions_history: updatedHistory
      });
      
      if (response.data) {
        toast.success("Revize kaydı güncellendi");
        setEditingRevisionHistory(null);
        setEditingRevisionHistoryPhotos({ before: [], after: [] });
        setSelectedRevisionDetail(null);
        
        // Parks listesini yenile
        window.dispatchEvent(new CustomEvent('refreshParks'));
        
        // Revize geçmişini yeniden yükle
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('refreshParks'));
        }, 500);
      }
    } catch (error) {
      console.error("Save edited revision history error:", error);
      console.error("Error response:", error.response);
      const errorMessage = error.response?.data?.detail || error.message || "Revize kaydı güncellenirken hata oluştu";
      toast.error(`Hata: ${errorMessage}`);
    } finally {
      setUploadingHistoryEditPhotos(false);
    }
  };

  return (
    <TabsContent value="revisions" className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">Revize Yönetimi</h2>
      </div>

      <Tabs defaultValue="new" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="new" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            <span>Yeni Revize</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <span>Revize Geçmişi</span>
          </TabsTrigger>
        </TabsList>

        {/* YENİ REVİZE SEKMESİ */}
        <TabsContent value="new" className="space-y-6">
          {/* "Yeni Revize Talebi" ekleme butonu — revizyon.olustur yetkisi gerekir.
              Sekme ve liste herkese (revizyon.gor) açık; yalnızca bu buton gizlenir. */}
          {!isGolgeAdmin && canCreateRevision && (
          <div className="flex items-center justify-end mb-4">
            <Button onClick={() => setRevisionDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Yeni Revize Talebi
            </Button>
          </div>
          )}

      {/* Filtreleme Alanı */}
      <Card className="bg-gray-50">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-600">Filtreler</span>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-xs">
                <X className="h-3 w-3 mr-1" />
                Temizle
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            {/* Kategori Filtresi */}
            <div className="flex-1 min-w-[150px]">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Kategori</label>
              <SearchableSelect
                className="w-full p-2 text-sm"
                value={filterCategory}
                onChange={(v) => setFilterCategory(v)}
                allLabel="Tüm Kategoriler"
                allValue="all"
                searchPlaceholder="Kategori ara..."
                options={categories.map(cat => ({ id: cat, name: cat }))}
              />
            </div>
            
            {/* Durum Filtresi */}
            <div className="flex-1 min-w-[150px]">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Durum</label>
              <SearchableSelect
                className="w-full p-2 text-sm"
                value={filterStatus}
                onChange={(v) => setFilterStatus(v)}
                allLabel="Tüm Durumlar"
                allValue="all"
                searchPlaceholder="Durum ara..."
                options={statuses.map(status => ({ id: status, name: status }))}
              />
            </div>
            
            {/* Aciliyet Filtresi */}
            <div className="flex-1 min-w-[150px]">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Aciliyet</label>
              <SearchableSelect
                className="w-full p-2 text-sm"
                value={filterUrgency}
                onChange={(v) => setFilterUrgency(v)}
                allLabel="Tüm Aciliyetler"
                allValue="all"
                searchPlaceholder="Aciliyet ara..."
                options={urgencies.map(urg => ({ id: urg, name: urg }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Revize Talepleri ({filteredRevisions.length}
            {hasFilters && ` / ${revisions.length}`})
          </CardTitle>
          <CardDescription>
            Park bakım ve onarım talepleri - Hiyerarşik onay sistemi
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingRevisions ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-4 text-gray-600">Yükleniyor...</p>
            </div>
          ) : filteredRevisions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Edit className="h-16 w-16 mx-auto mb-4 opacity-30" />
              {hasFilters ? (
                <>
                  <p className="text-lg font-medium mb-2">Filtrelere uygun talep bulunamadı</p>
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Filtreleri Temizle
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium mb-2">Henüz revize talebi yok</p>
                  <p className="text-sm">İlk talebi oluşturmak için yukarıdaki butona tıklayın</p>
                </>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Park</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Aciliyet</TableHead>
                  <TableHead>Talep Eden</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRevisions.map((revision) => (
                  <TableRow key={revision.id}>
                    <TableCell className="font-medium">{revision.park_name}</TableCell>
                    <TableCell>{revision.category}</TableCell>
                    <TableCell>{revision.title}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {(() => {
                          const statusLabel = displayRevisionStatus(revision);
                          return (
                        <Badge 
                          variant="outline"
                          className={
                            statusLabel === "Onaylandı" ? "bg-green-100 text-green-800 border-green-300" :
                            statusLabel === "Reddedildi" ? "bg-red-100 text-red-800 border-red-300" :
                            statusLabel === "Tamamlandı" ? "bg-blue-100 text-blue-800 border-blue-300" :
                            statusLabel === "Ertelendi" ? "bg-orange-100 text-orange-800 border-orange-300" :
                            statusLabel === "Onay Bekliyor" ? "bg-yellow-100 text-yellow-800 border-yellow-300" :
                            "bg-gray-100 text-gray-800 border-gray-300"
                          }
                        >
                          {statusLabel === "Onaylandı" ? "✅ " : ""}
                          {statusLabel === "Reddedildi" ? "❌ " : ""}
                          {statusLabel === "Ertelendi" ? "⏰ " : ""}
                          {statusLabel === "Tamamlandı" ? "✔️ " : ""}
                          {statusLabel === "Onay Bekliyor" ? "⏳ " : ""}
                          {statusLabel}
                        </Badge>
                          );
                        })()}
                        {(() => {
                          const reviewer = currentReviewerInfo(revision);
                          if (!reviewer) return null;
                          return (
                            <span className="text-xs text-gray-600">
                              Şu an: {reviewer.levelLabel}{reviewer.name ? ` - ${reviewer.name}` : ""}
                            </span>
                          );
                        })()}
                        {displayRevisionStatus(revision) === "Ertelendi" && revision.postponed_until && (
                          <span className="text-xs text-orange-600 font-medium">
                            📅 {new Date(revision.postponed_until).toLocaleDateString('tr-TR')}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {revision.urgency ? (
                        <Badge 
                          style={{
                            backgroundColor: 
                              revision.urgency === "ÇOK ACİL" ? "#dc2626" :
                              revision.urgency === "ACİL" ? "#f97316" :
                              revision.urgency === "ORTA" ? "#eab308" : "#22c55e",
                            color: "white",
                            border: "2px solid " + (
                              revision.urgency === "ÇOK ACİL" ? "#991b1b" :
                              revision.urgency === "ACİL" ? "#c2410c" :
                              revision.urgency === "ORTA" ? "#a16207" : "#15803d"
                            )
                          }}
                        >
                          {revision.urgency === "ÇOK ACİL" ? "🔴" :
                           revision.urgency === "ACİL" ? "🟠" :
                           revision.urgency === "ORTA" ? "🟡" : "🟢"} {revision.urgency}
                        </Badge>
                      ) : "-"}
                    </TableCell>
                    <TableCell>{revision.created_by_name}</TableCell>
                    <TableCell>{new Date(revision.created_at).toLocaleDateString('tr-TR')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          type="button"
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            if (e?.preventDefault) e.preventDefault();
                            if (e?.stopPropagation) e.stopPropagation();
                            setSelectedRevision(revision);
                          }}
                        >
                          Detay
                        </Button>
                        {/* Silme butonu sadece müdür için */}
                        {(isAdmin || isMudur) && onDeleteRevision && (
                          <Button 
                            type="button"
                            size="sm" 
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              if (e?.preventDefault) e.preventDefault();
                              if (e?.stopPropagation) e.stopPropagation();
                              onDeleteRevision(revision);
                            }}
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* REVİZE GEÇMİŞİ SEKMESİ */}
        <TabsContent value="history" className="space-y-6">
          {/* Filtreleme Alanı */}
          <Card className="bg-gray-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-600">Filtreler</span>
                {hasHistoryFilters && (
                  <Button variant="ghost" size="sm" onClick={clearHistoryFilters} className="ml-auto text-xs">
                    <X className="h-3 w-3 mr-1" />
                    Temizle
                  </Button>
                )}
              </div>
              
              {/* Arama Kutusu */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Park adı, kategori, açıklama veya mahalle ile ara..."
                    value={historySearchTerm}
                    onChange={(e) => setHistorySearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchHistory();
                      }
                    }}
                    className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Park Filtresi */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Park</label>
                  <SearchableSelect
                    className="w-full p-2 text-sm"
                    value={historyFilterPark}
                    onChange={(v) => setHistoryFilterPark(v)}
                    allLabel="Tüm Parklar"
                    allValue="all"
                    searchPlaceholder="Park ara..."
                    options={uniqueParks.map(park => ({
                      id: park.id,
                      name: `${park.name}${park.neighborhood ? ` (${park.neighborhood})` : ''}`,
                    }))}
                  />
                </div>
                
                {/* Kategori Filtresi */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Revize Türü</label>
                  <SearchableSelect
                    className="w-full p-2 text-sm"
                    value={historyFilterCategory}
                    onChange={(v) => setHistoryFilterCategory(v)}
                    allLabel="Tüm Türler"
                    allValue="all"
                    searchPlaceholder="Tür ara..."
                    options={uniqueCategories.map(cat => ({ id: cat, name: cat }))}
                  />
                </div>
                
                {/* Tarih Aralığı */}
                <div className="col-span-2">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Tarih Aralığı</label>
                  <DateRangePicker
                    startDate={historyFilterDateFrom}
                    endDate={historyFilterDateTo}
                    onChange={({ start, end }) => {
                      setHistoryFilterDateFrom(start);
                      setHistoryFilterDateTo(end);
                    }}
                    placeholder="Tarih aralığı"
                  />
                </div>
              </div>
              
              {/* Ara Butonu */}
              <div className="flex items-end">
                <Button 
                  onClick={handleSearchHistory}
                  className="w-full"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Ara
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Revize Geçmişi ({filteredRevisionHistory.length}
                {hasHistoryFilters && ` / ${allRevisionHistory.length}`})
              </CardTitle>
              <CardDescription>
                Tamamlanmış revize kayıtları - Hangi parkta, hangi tarihte, nasıl bir revize yapıldığı
              </CardDescription>
            </CardHeader>
            <CardContent>
              {allRevisionHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium mb-2">Henüz revize geçmişi bulunmamaktadır</p>
                  <p className="text-sm">Tamamlanan revizeler burada görüntülenecektir</p>
                </div>
              ) : filteredRevisionHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  {hasHistoryFilters ? (
                    <>
                      <p className="text-lg font-medium mb-2">Filtrelere uygun revize geçmişi bulunamadı</p>
                      <Button variant="outline" size="sm" onClick={clearHistoryFilters}>
                        Filtreleri Temizle
                      </Button>
                    </>
                  ) : (
                    <p className="text-lg font-medium mb-2">Henüz revize geçmişi bulunmamaktadır</p>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Park</TableHead>
                      <TableHead>Revize Türü</TableHead>
                      <TableHead>Yapılan İşlem</TableHead>
                      <TableHead className="text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRevisionHistory.map((rev, idx) => {
                      const revDate = rev.revision_date || rev.created_at;
                      return (
                        <TableRow key={rev.revision_id || idx}>
                          <TableCell className="font-medium">
                            {revDate ? formatDate(revDate) : 'Tarih belirtilmemiş'}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{rev.park_name}</div>
                              {rev.park_neighborhood && (
                                <div className="text-xs text-gray-500">{rev.park_neighborhood}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                              {rev.category || 'Kategori belirtilmemiş'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {rev.description ? (
                              <span className="text-sm text-gray-600 line-clamp-2">
                                {rev.description.length > 100 
                                  ? `${rev.description.substring(0, 100)}...` 
                                  : rev.description}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">Açıklama yok</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const park = filteredParks.find(p => p.id === rev.park_id);
                                  setSelectedRevisionDetail({ ...rev, park });
                                }}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Detaylar
                              </Button>
                              {(isAdmin || isMudur) && onDeleteCompletedRevisionHistory && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Geçmiş revize kaydını sil"
                                  onClick={() => openHistoryDeleteDialog(rev)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Revize Geçmişi Silme Onayı (Uygulama içi 2 adım) */}
      <DraggableModal
        isOpen={historyDeleteDialog.open}
        onClose={closeHistoryDeleteDialog}
        title={historyDeleteDialog.step === 1 ? "Geçmiş Revize Kaydını Sil" : "Son Onay"}
        subtitle={historyDeleteDialog.parkName ? `${historyDeleteDialog.parkName} - ${historyDeleteDialog.category || "Revize"}` : ""}
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          {historyDeleteDialog.step === 1 ? (
            <p className="text-sm text-gray-700">
              Bu geçmiş revize kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
          ) : (
            <p className="text-sm text-red-700 font-medium">
              Son onay: Bu kayıt kalıcı olarak silinecek ve tüm panellerden kaldırılacak.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closeHistoryDeleteDialog}>
              İptal
            </Button>
            {historyDeleteDialog.step === 1 ? (
              <Button
                variant="destructive"
                onClick={() => setHistoryDeleteDialog((prev) => ({ ...prev, step: 2 }))}
              >
                Devam Et
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleConfirmHistoryDelete}>
                Evet, Kalıcı Sil
              </Button>
            )}
          </div>
        </div>
      </DraggableModal>

      {/* Revize Detayları Dialog'u */}
      <DraggableModal
        isOpen={selectedRevisionDetail !== null}
        onClose={() => setSelectedRevisionDetail(null)}
        title="Revize Detayları"
        subtitle={selectedRevisionDetail ? `${selectedRevisionDetail.park_name} - ${selectedRevisionDetail.category || 'Kategori belirtilmemiş'}` : ''}
        maxWidth="max-w-4xl"
      >
          {selectedRevisionDetail && (
            <div className="space-y-6">
              {/* Park Bilgileri */}
              {selectedRevisionDetail.park && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold mb-3 text-gray-700">Park Bilgileri</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Park Adı:</span>
                      <span className="ml-2 text-gray-800">{selectedRevisionDetail.park.name}</span>
                    </div>
                    {selectedRevisionDetail.park.neighborhood && (
                      <div>
                        <span className="font-medium text-gray-600">Mahalle:</span>
                        <span className="ml-2 text-gray-800">{selectedRevisionDetail.park.neighborhood}</span>
                      </div>
                    )}
                    {selectedRevisionDetail.park.address && (
                      <div className="col-span-2">
                        <span className="font-medium text-gray-600">Adres:</span>
                        <span className="ml-2 text-gray-800">{selectedRevisionDetail.park.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Revize Bilgileri */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-gray-700">Revize Bilgileri</h3>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-600">Tarih:</span>
                    <span className="ml-2 text-gray-800">
                      {selectedRevisionDetail.revision_date 
                        ? formatDate(selectedRevisionDetail.revision_date)
                        : selectedRevisionDetail.created_at 
                          ? formatDate(selectedRevisionDetail.created_at)
                          : 'Tarih belirtilmemiş'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Revize Türü:</span>
                    <Badge className="ml-2 bg-blue-100 text-blue-800 border-blue-300">
                      {selectedRevisionDetail.category || 'Kategori belirtilmemiş'}
                    </Badge>
                  </div>
                  {selectedRevisionDetail.description && (
                    <div>
                      <span className="font-medium text-gray-600">Açıklama:</span>
                      <p className="mt-1 text-gray-800 bg-gray-50 p-3 rounded border">
                        {selectedRevisionDetail.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Düzenle Butonu - Sadece müdür ve müdür yardımcısı için - Revize Bilgileri'nin hemen altında */}
              {canEditRevisionHistory && selectedRevisionDetail?.park_id && !editingRevisionHistory && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!selectedRevisionDetail?.park_id || !selectedRevisionDetail?.revision_id) {
                        toast.error("Revize kaydı bilgileri eksik");
                        return;
                      }
                      
                      setEditingRevisionHistory({
                        revision_id: selectedRevisionDetail.revision_id,
                        park_id: selectedRevisionDetail.park_id,
                        date: selectedRevisionDetail.revision_date || selectedRevisionDetail.created_at || new Date().toISOString().split('T')[0],
                        category: selectedRevisionDetail.category || '',
                        description: selectedRevisionDetail.description || ''
                      });
                      
                      const photosBefore = Array.isArray(selectedRevisionDetail.photos) ? [] : (selectedRevisionDetail.photos?.before || []);
                      const photosAfter = Array.isArray(selectedRevisionDetail.photos) ? [] : (selectedRevisionDetail.photos?.after || []);
                      setEditingRevisionHistoryPhotos({
                        before: [...photosBefore],
                        after: [...photosAfter]
                      });
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Düzenle
                  </Button>
                </div>
              )}

              {/* Sadece Görüntüleme Uyarısı - Yetkisi olmayanlar için */}
              {!canEditRevisionHistory && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Sadece Görüntüleme</span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Bu revize kaydını sadece görüntüleyebilirsiniz. Düzenleme yetkiniz bulunmamaktadır.
                  </p>
                </div>
              )}

              {/* Fotoğraflar */}
              {(() => {
                const photosBefore = Array.isArray(selectedRevisionDetail.photos) ? [] : (selectedRevisionDetail.photos?.before || []);
                const photosAfter = Array.isArray(selectedRevisionDetail.photos) ? [] : (selectedRevisionDetail.photos?.after || []);
                
                if (photosBefore.length === 0 && photosAfter.length === 0) return null;
                
                return (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-3 text-gray-700">Fotoğraflar</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Eskisi */}
                      {photosBefore.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                            <Label className="text-sm font-semibold text-gray-700">Eskisi</Label>
                            <span className="text-xs text-gray-500">({photosBefore.length} fotoğraf)</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {photosBefore.map((photo, photoIdx) => (
                              <img
                                key={photoIdx}
                                src={resolvePhotoUrl(photo)}
                                alt={`Eski fotoğraf ${photoIdx + 1}`}
                                className="w-full h-24 object-cover rounded border border-gray-200 cursor-zoom-in hover:opacity-90 transition-opacity"
                                onClick={() => openPhotoViewer(photosBefore, photoIdx, "Eskisi")}
                                onError={(e) => {
                                  e.currentTarget.style.opacity = "0.5";
                                  e.currentTarget.title = "Fotoğraf yüklenemedi";
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Yenisi */}
                      {photosAfter.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                            <Label className="text-sm font-semibold text-green-700">Yenisi</Label>
                            <span className="text-xs text-gray-500">({photosAfter.length} fotoğraf)</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {photosAfter.map((photo, photoIdx) => (
                              <img
                                key={photoIdx}
                                src={resolvePhotoUrl(photo)}
                                alt={`Yeni fotoğraf ${photoIdx + 1}`}
                                className="w-full h-24 object-cover rounded border border-green-200 cursor-zoom-in hover:opacity-90 transition-opacity"
                                onClick={() => openPhotoViewer(photosAfter, photoIdx, "Yenisi")}
                                onError={(e) => {
                                  e.currentTarget.style.opacity = "0.5";
                                  e.currentTarget.title = "Fotoğraf yüklenemedi";
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
      </DraggableModal>

      {/* Fotoğraf Önizleme (Büyük) */}
      <DraggableModal
        isOpen={photoViewer.open}
        onClose={() => setPhotoViewer({ open: false, photos: [], index: 0, title: "Revize Fotoğrafı" })}
        title={photoViewer.title}
        subtitle={`${photoViewer.photos.length > 0 ? photoViewer.index + 1 : 0} / ${photoViewer.photos.length}`}
        maxWidth="max-w-5xl"
      >
        {photoViewer.open && photoViewer.photos.length > 0 && (
          <div className="space-y-4">
            <div className="w-full flex justify-center bg-black/5 rounded-lg p-2">
              <img
                src={resolvePhotoUrl(photoViewer.photos[photoViewer.index])}
                alt={`Fotoğraf ${photoViewer.index + 1}`}
                className="max-h-[70vh] w-auto max-w-full object-contain rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() =>
                  setPhotoViewer((prev) => ({
                    ...prev,
                    index: (prev.index - 1 + prev.photos.length) % prev.photos.length,
                  }))
                }
              >
                Önceki
              </Button>
              <span className="text-sm text-gray-600">
                {photoViewer.index + 1} / {photoViewer.photos.length}
              </span>
              <Button
                variant="outline"
                onClick={() =>
                  setPhotoViewer((prev) => ({
                    ...prev,
                    index: (prev.index + 1) % prev.photos.length,
                  }))
                }
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </DraggableModal>

      {/* Revize Geçmişi Düzenleme Dialog'u */}
      <DraggableModal
        isOpen={editingRevisionHistory !== null}
        onClose={() => {
          setEditingRevisionHistory(null);
          setEditingRevisionHistoryPhotos({ before: [], after: [] });
        }}
        title="Revize Geçmişini Düzenle"
        subtitle="Revize kaydını düzenleyebilirsiniz. Yapılan değişiklikler kaydedilecektir."
        maxWidth="max-w-4xl"
      >
        {editingRevisionHistory && (
          <div className="space-y-6">
            {/* Tarih */}
            <div>
              <DatePicker
                id="edit-history-revision-date"
                label="Revize Tarihi"
                required
                value={editingRevisionHistory.date}
                onChange={(value) => setEditingRevisionHistory(prev => ({ ...prev, date: value }))}
              />
            </div>
            
            {/* Kategori */}
            <div>
              <Label htmlFor="edit-history-revision-category">Revize Türü *</Label>
              <SearchableSelect
                value={editingRevisionHistory.category}
                onChange={(value) => setEditingRevisionHistory(prev => ({ ...prev, category: value }))}
                className="mt-1"
                showAllOption={false}
                placeholder="Revize türü seçin"
                searchPlaceholder="Revize türü ara..."
                options={REVISION_TYPES.map((cat) => ({ id: cat, name: cat }))}
              />
            </div>
            
            {/* Açıklama */}
            <div>
              <Label htmlFor="edit-history-revision-description">Açıklama</Label>
              <Textarea
                id="edit-history-revision-description"
                value={editingRevisionHistory.description}
                onChange={(e) => setEditingRevisionHistory(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={4}
                placeholder="Revize açıklaması..."
              />
            </div>
            
            {/* Fotoğraflar - Eskisi */}
            <div>
              <Label>Eski Fotoğraflar</Label>
              <div className="mt-2 space-y-3">
                {editingRevisionHistoryPhotos.before.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {editingRevisionHistoryPhotos.before.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={resolvePhotoUrl(photo)}
                          alt={`Eski fotoğraf ${index + 1}`}
                          className="w-full h-20 object-cover rounded border border-gray-200"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-0 right-0 h-6 w-6 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-md z-10"
                          onClick={() => handleRemoveHistoryEditPhoto('before', index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div>
                  <Label htmlFor="edit-history-photo-before" className="text-sm font-medium text-gray-700 mb-2 block">
                    Dosyaları Seç (Eski)
                  </Label>
                  <div className="relative">
                    <Input
                      id="edit-history-photo-before"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleUploadHistoryEditPhoto(e, 'before')}
                      disabled={uploadingHistoryEditPhotos || editingRevisionHistoryPhotos.before.length >= 10}
                      className="hidden"
                    />
                    <label
                      htmlFor="edit-history-photo-before"
                      className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ${
                        (uploadingHistoryEditPhotos || editingRevisionHistoryPhotos.before.length >= 10) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Fotoğraf Seç
                    </label>
                    <span className="ml-3 text-sm text-gray-500">
                      {uploadingHistoryEditPhotos ? 'Yükleniyor...' : editingRevisionHistoryPhotos.before.length >= 10 ? 'Maksimum fotoğraf sayısına ulaşıldı' : 'Dosya seçilmedi'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {editingRevisionHistoryPhotos.before.length}/10 fotoğraf (Maksimum 10)
                  </p>
                </div>
              </div>
            </div>
            
            {/* Fotoğraflar - Yenisi */}
            <div>
              <Label>Yeni Fotoğraflar</Label>
              <div className="mt-2 space-y-3">
                {editingRevisionHistoryPhotos.after.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {editingRevisionHistoryPhotos.after.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={resolvePhotoUrl(photo)}
                          alt={`Yeni fotoğraf ${index + 1}`}
                          className="w-full h-20 object-cover rounded border border-green-200"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-0 right-0 h-6 w-6 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-md z-10"
                          onClick={() => handleRemoveHistoryEditPhoto('after', index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div>
                  <Label htmlFor="edit-history-photo-after" className="text-sm font-medium text-gray-700 mb-2 block">
                    Dosyaları Seç (Yeni)
                  </Label>
                  <div className="relative">
                    <Input
                      id="edit-history-photo-after"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleUploadHistoryEditPhoto(e, 'after')}
                      disabled={uploadingHistoryEditPhotos || editingRevisionHistoryPhotos.after.length >= 10}
                      className="hidden"
                    />
                    <label
                      htmlFor="edit-history-photo-after"
                      className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ${
                        (uploadingHistoryEditPhotos || editingRevisionHistoryPhotos.after.length >= 10) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Fotoğraf Seç
                    </label>
                    <span className="ml-3 text-sm text-gray-500">
                      {uploadingHistoryEditPhotos ? 'Yükleniyor...' : editingRevisionHistoryPhotos.after.length >= 10 ? 'Maksimum fotoğraf sayısına ulaşıldı' : 'Dosya seçilmedi'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {editingRevisionHistoryPhotos.after.length}/10 fotoğraf (Maksimum 10)
                  </p>
                </div>
              </div>
            </div>
            
            {/* Butonlar */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingRevisionHistory(null);
                  setEditingRevisionHistoryPhotos({ before: [], after: [] });
                }}
                disabled={uploadingHistoryEditPhotos}
              >
                İptal
              </Button>
              <Button
                onClick={handleSaveHistoryEditedRevision}
                disabled={uploadingHistoryEditPhotos || !editingRevisionHistory.date || !editingRevisionHistory.category}
              >
                {uploadingHistoryEditPhotos ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          </div>
        )}
      </DraggableModal>
    </TabsContent>
  );
}
